/**
 * MemoryImportModal — MD files in, picked memories out. Three steps in one
 * modal: pick files, read them (one LLM call, skeleton while it runs), then
 * tick which candidates to keep. The files are read in the browser and posted
 * as text; nothing is stored until the user confirms, and confirming closes
 * the modal at once (the rows appear optimistically behind it).
 */
import { useMemo, useRef, useState } from "react";
import { FileText, FolderOpen, Pencil, Upload, X } from "lucide-react";
import type { UseMutationResult } from "@tanstack/react-query";
import { cn } from "@/utils/helpers";
import {
  MEMORY_IMPORT_MAX_CHARS,
  MEMORY_IMPORT_MAX_FILES,
  memoryErrorMessage,
  type MemoryImportFile,
  type MemoryImportPreview,
} from "@/app/routes/dashboard/settings/api/memoryMutations";
import { KitButton } from "../../kit/Button";
import { kitBareFieldClass } from "../../kit/Field";
import { KitCheckbox } from "../../kit/Checkbox";
import { KitModal } from "../../kit/Modal";

const ACCEPT = ".md,.markdown,.txt,text/markdown,text/plain";
const TEXT_FILE = /\.(md|markdown|txt)$/i;

type Row = {
  body: string;
  recommended: boolean;
  reason: string | null;
  selected: boolean;
};

function ReadingSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-label="Reading your files">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex animate-pulse items-start gap-3 rounded-card border border-co-line bg-co-surface p-3">
          <div className="mt-0.5 size-4.5 shrink-0 bg-co-hover" />
          <div className="flex flex-1 flex-col gap-2">
            <div className="h-3 w-full bg-co-hover" />
            <div className={cn("h-3 bg-co-hover", i % 2 ? "w-2/3" : "w-1/2")} />
          </div>
        </div>
      ))}
    </div>
  );
}

function CandidateRow({
  row,
  maxLength,
  onToggle,
  onEdit,
}: {
  row: Row;
  maxLength: number;
  onToggle: (selected: boolean) => void;
  onEdit: (body: string) => void;
}) {
  const [editing, setEditing] = useState(false);

  return (
    <li
      className={cn(
        "flex items-start gap-3 rounded-card border bg-co-surface p-3 transition-colors",
        row.selected ? "border-co-accent-border" : "border-co-line2",
      )}
    >
      <KitCheckbox
        isSelected={row.selected}
        onChange={onToggle}
        aria-label={row.selected ? "Don't import this memory" : "Import this memory"}
        className="mt-0.5"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {editing ? (
          <textarea
            value={row.body}
            onChange={(e) => onEdit(e.target.value.slice(0, maxLength))}
            onBlur={() => setEditing(false)}
            maxLength={maxLength}
            rows={3}
            autoFocus
            className={cn(kitBareFieldClass, "block w-full resize-none px-3 py-2 text-[13px] leading-relaxed")}
          />
        ) : (
          <p className={cn("text-[13px] leading-relaxed", row.selected ? "text-co-text" : "text-co-muted")}>
            {row.body}
          </p>
        )}
        {!row.recommended && row.reason && (
          <span className="text-[11px] text-co-faint">Not recommended — {row.reason}</span>
        )}
      </div>
      {!editing && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label="Edit before importing"
          className="co-glass-control flex size-7 shrink-0 items-center justify-center rounded-pill text-co-text2 transition-colors hover:text-co-text"
        >
          <Pencil className="size-3" />
        </button>
      )}
    </li>
  );
}

export default function MemoryImportModal({
  onClose,
  preview,
  onSave,
  maxLength,
  description,
}: {
  onClose: () => void;
  preview: UseMutationResult<MemoryImportPreview, unknown, MemoryImportFile[]>;
  /** Fire-and-forget: the caller's mutation is optimistic and toasts on failure. */
  onSave: (bodies: string[]) => void;
  maxLength: number;
  description: string;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<MemoryImportFile[]>([]);
  const [pasted, setPasted] = useState("");
  const [pickError, setPickError] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [headroom, setHeadroom] = useState(0);

  const hasPasted = pasted.trim().length > 0;
  // Pasted text goes through the same pipeline as a file named "Pasted text".
  const allFiles = useMemo<MemoryImportFile[]>(
    () => (hasPasted ? [...files, { name: "Pasted text", content: pasted }] : files),
    [files, hasPasted, pasted],
  );
  const totalChars = useMemo(() => allFiles.reduce((n, f) => n + f.content.length, 0), [allFiles]);
  const selectedCount = rows?.filter((r) => r.selected && r.body.trim()).length ?? 0;
  const overHeadroom = selectedCount > headroom;

  const addFiles = async (list: FileList | null) => {
    if (!list?.length) return;
    // A folder pick hands over everything inside it; keep the text files.
    const picked = Array.from(list).filter((f) => TEXT_FILE.test(f.name));
    if (picked.length === 0) {
      setPickError("No .md or .txt files in that selection.");
      return;
    }
    const read = await Promise.all(
      picked.map(async (f) => ({
        name: (f.webkitRelativePath || f.name).slice(0, 200),
        content: await f.text(),
      })),
    );
    setFiles((prev) => {
      const byName = new Map(prev.map((f) => [f.name, f]));
      for (const f of read) byName.set(f.name, f);
      return [...byName.values()];
    });
    setPickError(null);
  };

  const removeFile = (name: string) => setFiles((prev) => prev.filter((f) => f.name !== name));

  const pickProblem =
    allFiles.length > MEMORY_IMPORT_MAX_FILES
      ? `Import at most ${MEMORY_IMPORT_MAX_FILES} files at a time.`
      : totalChars > MEMORY_IMPORT_MAX_CHARS
        ? `These files are too long to import at once (${totalChars.toLocaleString()} of ${MEMORY_IMPORT_MAX_CHARS.toLocaleString()} characters). Remove a few.`
        : allFiles.length > 0 && allFiles.every((f) => !f.content.trim())
          ? "These files are empty."
          : null;

  const read = () => {
    preview.mutate(allFiles, {
      onSuccess: (result) => {
        let room = result.headroom;
        setHeadroom(result.headroom);
        setRows(
          result.candidates.map((c) => {
            // Recommended ones start ticked, up to the free slots.
            const selected = c.recommended && room > 0;
            if (selected) room -= 1;
            return { ...c, selected };
          }),
        );
      },
    });
  };

  const updateRow = (index: number, patch: Partial<Row>) =>
    setRows((prev) => prev?.map((r, i) => (i === index ? { ...r, ...patch } : r)) ?? prev);

  const selectGroup = (recommendedGroup: boolean, selected: boolean) =>
    setRows(
      (prev) =>
        prev?.map((r) => (r.recommended === recommendedGroup ? { ...r, selected } : r)) ?? prev,
    );

  const save = () => {
    if (!rows) return;
    const bodies = rows.filter((r) => r.selected).map((r) => r.body.trim()).filter(Boolean);
    if (bodies.length === 0 || overHeadroom) return;
    onSave(bodies);
    onClose();
  };

  const recommended = rows?.filter((r) => r.recommended) ?? [];
  const notRecommended = rows?.filter((r) => !r.recommended) ?? [];
  const indexOf = (row: Row) => rows!.indexOf(row);

  return (
    <KitModal
      isOpen
      onOpenChange={(next) => (next ? undefined : onClose())}
      title="Import memories"
      description={description}
      // A fixed-height column: the header and footer stay put and only the
      // candidate list scrolls, however many memories the files held.
      modalClassName="!w-[min(calc(100vw-2rem),640px)] !max-w-[640px] !max-h-[min(90vh,760px)] flex flex-col !overflow-hidden"
    >
      <div className="flex min-h-0 w-full flex-1 flex-col gap-4">
        {rows === null && !preview.isPending && (
          <>
            <div className="flex flex-col items-center gap-3 rounded-panel border border-dashed border-co-line2 bg-co-surface px-4 py-6 text-center">
              <Upload className="size-5 text-co-faint" />
              <p className="max-w-[440px] text-[13px] leading-relaxed text-co-muted">
                Pick .md files — a Claude memory folder, a CLAUDE.md, notes, or another assistant&apos;s
                memory export. They&apos;re read here in your browser; nothing is saved until you
                choose what to keep.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <KitButton type="button" variant="secondary" iconLeft={FileText} onClick={() => fileInputRef.current?.click()}>
                  Choose files
                </KitButton>
                <KitButton type="button" variant="secondary" iconLeft={FolderOpen} onClick={() => folderInputRef.current?.click()}>
                  Choose folder
                </KitButton>
              </div>
              <span className="text-section-label font-medium uppercase tracking-[0.14em] text-co-faint">
                or paste text
              </span>
              <textarea
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                placeholder="Paste memory markdown — e.g. what your assistant printed from the memory prompt."
                rows={4}
                aria-label="Paste memory text"
                className={cn(kitBareFieldClass, "block w-full resize-none px-3 py-2 text-left font-mono text-[12px] leading-relaxed text-co-text2")}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                multiple
                className="hidden"
                onChange={(e) => {
                  void addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <input
                // webkitdirectory is not in React's input typings; set it on the element.
                ref={(el) => {
                  folderInputRef.current = el;
                  el?.setAttribute("webkitdirectory", "");
                }}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  void addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>

            {allFiles.length > 0 && (
              <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                {allFiles.map((f) => (
                  <li key={f.name} className="flex items-center gap-2 rounded-card bg-co-surface px-3 py-1.5">
                    <FileText className="size-3.5 shrink-0 text-co-faint" />
                    <span className="min-w-0 flex-1 truncate text-[12px] text-co-text2">{f.name}</span>
                    <span className="shrink-0 text-[11px] tabular-nums text-co-faint">
                      {f.content.length.toLocaleString()} chars
                    </span>
                    <button
                      type="button"
                      onClick={() => (f.name === "Pasted text" ? setPasted("") : removeFile(f.name))}
                      aria-label={`Remove ${f.name}`}
                      className="flex size-6 shrink-0 items-center justify-center text-co-faint transition-colors hover:text-co-text"
                    >
                      <X className="size-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {(pickError || pickProblem) && (
              <p className="text-[13px] text-co-alert-ink">{pickError ?? pickProblem}</p>
            )}
            {preview.isError && (
              <p className="text-[13px] text-co-alert-ink">
                {memoryErrorMessage(preview.error, "Could not read these files.")}
              </p>
            )}

            <div className="flex gap-3 pt-2">
              <KitButton type="button" variant="ghost" onClick={onClose} className="flex-1">
                Cancel
              </KitButton>
              <KitButton
                type="button"
                onClick={read}
                isDisabled={allFiles.length === 0 || Boolean(pickProblem)}
                className="flex-1"
              >
                {hasPasted && files.length === 0
                  ? "Read text"
                  : allFiles.length > 1
                    ? `Read ${allFiles.length} files`
                    : "Read file"}
              </KitButton>
            </div>
          </>
        )}

        {preview.isPending && (
          <>
            <p className="text-[13px] text-co-muted">Reading your files for memories worth keeping…</p>
            <ReadingSkeleton />
          </>
        )}

        {rows !== null && !preview.isPending && (
          <>
            {rows.length === 0 ? (
              <p className="rounded-card bg-co-surface px-4 py-8 text-center text-[13px] text-co-faint">
                Nothing in these files reads as a memory worth keeping, or it&apos;s already saved.
              </p>
            ) : (
              // The one scroll box: it takes whatever height the modal has left,
              // so the slot count and the Import button are always on screen.
              <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain pr-2">
                {[
                  { title: "Recommended", group: recommended, recommendedGroup: true },
                  { title: "Probably not useful here", group: notRecommended, recommendedGroup: false },
                ]
                  .filter(({ group }) => group.length > 0)
                  .map(({ title, group, recommendedGroup }) => {
                    const allSelected = group.every((r) => r.selected);
                    return (
                      <section key={title} className="flex flex-col gap-2">
                        {/* Sticky, so "Select all" stays in reach while scrolling the group. */}
                        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 bg-co-surface-solid py-1">
                          <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-co-faint">
                            {title} · {group.length}
                          </span>
                          <button
                            type="button"
                            onClick={() => selectGroup(recommendedGroup, !allSelected)}
                            className="text-[12px] text-co-muted underline decoration-co-line2 underline-offset-4 transition-colors hover:text-co-text"
                          >
                            {allSelected ? "Select none" : "Select all"}
                          </button>
                        </div>
                        <ul className="co-stagger flex flex-col gap-2">
                          {group.map((row) => (
                            <CandidateRow
                              key={indexOf(row)}
                              row={row}
                              maxLength={maxLength}
                              onToggle={(selected) => updateRow(indexOf(row), { selected })}
                              onEdit={(body) => updateRow(indexOf(row), { body })}
                            />
                          ))}
                        </ul>
                      </section>
                    );
                  })}
              </div>
            )}

            <p className={cn("shrink-0 text-[12px]", overHeadroom ? "text-co-alert-ink" : "text-co-faint")}>
              {selectedCount} selected · {headroom} free {headroom === 1 ? "slot" : "slots"}
              {overHeadroom && " — untick some, or delete existing memories first."}
            </p>

            <div className="flex shrink-0 gap-3 pt-2">
              <KitButton
                type="button"
                variant="ghost"
                onClick={() => {
                  setRows(null);
                  preview.reset();
                }}
                className="flex-1"
              >
                Back
              </KitButton>
              <KitButton
                type="button"
                onClick={save}
                isDisabled={selectedCount === 0 || overHeadroom}
                className="flex-1"
              >
                {selectedCount > 0 ? `Import ${selectedCount}` : "Import"}
              </KitButton>
            </div>
          </>
        )}
      </div>
    </KitModal>
  );
}
